// @tennis-matches/services/trainerstunden/billing-cancelled-session-effects
// Auswirkung einer nachträglichen Session-Absage auf bereits erzeugte Abrechnungen:
// - Entwurf (draft): betroffene Positionen werden entfernt (Aufrufer nutzt removeBillingStatementDraftLines)
// - Versendet/bezahlt (sent/partially_paid/paid): Rechnung bleibt unverändert, optionale Gutschrift

import { BILLING_SENT_STATEMENT_STATUSES } from "./billing-session-lock.js";
import { isCancelledSession } from "./session-status.js";

const SENT = new Set(BILLING_SENT_STATEMENT_STATUSES);

function __asStr(v) {
  return String(v ?? "").trim();
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * @param {{
 *   sessionId: string,
 *   billingStatements?: unknown[],
 *   billingStatementItems?: unknown[],
 * }} args
 * @returns {{
 *   draftLineRemovals: Array<{ statementId: string, itemIds: string[], amountEur: number }>,
 *   creditCandidates: Array<{ statementId: string, status: string, billedEur: number }>,
 *   frozenTotalEur: number,
 *   hasDraftImpact: boolean,
 *   hasFrozenImpact: boolean,
 * }}
 */
export function analyzeCancelledSessionBillingImpact({
  sessionId,
  billingStatements,
  billingStatementItems,
} = {}) {
  const sid = __asStr(sessionId);
  const empty = {
    draftLineRemovals: [],
    creditCandidates: [],
    frozenTotalEur: 0,
    hasDraftImpact: false,
    hasFrozenImpact: false,
  };
  if (!sid) return empty;

  const statements = Array.isArray(billingStatements) ? billingStatements : [];
  const items = Array.isArray(billingStatementItems)
    ? billingStatementItems
    : [];
  const stmtById = new Map(statements.map((s) => [__asStr(s?.id), s]));

  /** @type {Map<string, { itemIds: string[], amountEur: number }>} */
  const draftByStatement = new Map();
  /** @type {Map<string, number>} */
  const frozenByStatement = new Map();

  for (const it of items) {
    if (__asStr(it?.session_id) !== sid) continue;
    const stId = __asStr(it?.statement_id);
    if (!stId) continue;
    const st = stmtById.get(stId);
    if (!st) continue;
    const status = __asStr(st?.status);
    const amount = Number(it?.amount_eur) || 0;
    if (status === "draft") {
      const entry = draftByStatement.get(stId) || { itemIds: [], amountEur: 0 };
      const iid = __asStr(it?.id);
      if (iid) entry.itemIds.push(iid);
      entry.amountEur = round2(entry.amountEur + amount);
      draftByStatement.set(stId, entry);
    } else if (SENT.has(status)) {
      frozenByStatement.set(
        stId,
        round2((frozenByStatement.get(stId) || 0) + amount),
      );
    }
  }

  const draftLineRemovals = [...draftByStatement.entries()]
    .filter(([, v]) => v.itemIds.length > 0)
    .map(([statementId, v]) => ({
      statementId,
      itemIds: v.itemIds,
      amountEur: v.amountEur,
    }));

  const creditCandidates = [...frozenByStatement.entries()]
    .filter(([, billed]) => billed > 0.004)
    .map(([statementId, billed]) => ({
      statementId,
      status: __asStr(stmtById.get(statementId)?.status),
      billedEur: round2(billed),
    }));

  const frozenTotalEur = round2(
    creditCandidates.reduce((s, c) => s + c.billedEur, 0),
  );

  return {
    draftLineRemovals,
    creditCandidates,
    frozenTotalEur,
    hasDraftImpact: draftLineRemovals.length > 0,
    hasFrozenImpact: creditCandidates.length > 0,
  };
}

function isSessionUnbillableInDraft(session) {
  if (!session || typeof session !== "object") return false;
  if (isCancelledSession(session)) return true;
  return !session.attendance_confirmed;
}

/**
 * Sammelt alle Entwurfs-Positionen (kind='session'), deren Session nicht abrechenbar ist
 * (storniert oder Anwesenheit nicht bestätigt), gruppiert nach Statement.
 * Selbstheilung beim Laden: bereinigt Altlasten nach Absage oder Bestätigungs-Reset.
 *
 * @param {{
 *   sessions?: unknown[],
 *   billingStatements?: unknown[],
 *   billingStatementItems?: unknown[],
 * }} args
 * @returns {Array<{ statementId: string, itemIds: string[] }>}
 */
export function collectCancelledSessionDraftLineRemovals({
  sessions,
  billingStatements,
  billingStatementItems,
} = {}) {
  const sess = Array.isArray(sessions) ? sessions : [];
  const statements = Array.isArray(billingStatements) ? billingStatements : [];
  const items = Array.isArray(billingStatementItems)
    ? billingStatementItems
    : [];

  const draftStmtIds = new Set(
    statements
      .filter((s) => __asStr(s?.status) === "draft")
      .map((s) => __asStr(s?.id))
      .filter(Boolean),
  );
  if (!draftStmtIds.size) return [];

  const unbillableSessionIds = new Set(
    sess
      .filter((s) => isSessionUnbillableInDraft(s))
      .map((s) => __asStr(s?.id))
      .filter(Boolean),
  );
  if (!unbillableSessionIds.size) return [];

  const byStatement = new Map();
  for (const it of items) {
    if (__asStr(it?.kind) !== "session") continue;
    const sid = __asStr(it?.session_id);
    if (!sid || !unbillableSessionIds.has(sid)) continue;
    const stId = __asStr(it?.statement_id);
    if (!draftStmtIds.has(stId)) continue;
    const iid = __asStr(it?.id);
    if (!iid) continue;
    const arr = byStatement.get(stId) || [];
    arr.push(iid);
    byStatement.set(stId, arr);
  }

  return [...byStatement.entries()].map(([statementId, itemIds]) => ({
    statementId,
    itemIds,
  }));
}
